import { AfterViewInit, Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TrackByFunction, inject } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { Utilities } from "@tylertech/shared-module";
import { ClassMetadata, PropertyMetadata } from "@tylertech/tyler-api-types";
import { SecurityService } from "@tylertech/core-module";
import
{
    IAugmentedWithParent,
    ModifierCommandArgs,
    MetadataIntermediary,
    RepositoryFormData,
    ModelChangeArg
} from "../../interfaces";
import { MetadataService, MetadataPropertyService, MetadataEventService } from "../../services";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { filter } from "rxjs/operators";

interface IPanelRow
{
    Index: number;
    Intermediaries: MetadataIntermediary[];
}

@Component({
    selector: "metadata-panel",
    templateUrl: "./metadataPanel.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataPanelComponent implements OnInit, AfterViewInit, DoCheck, OnChanges
{
    modelGroup = inject(NgModelGroup);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $metadataService = inject(MetadataService);
    private $metadataEventService = inject(MetadataEventService);
    private readonly $securityService = inject(SecurityService);

    @Input()
    model: IAugmentedWithParent;

    @Input()
    metadata: ClassMetadata;

    @Input()
    actionCount: number; //For summary panels to align the summary fields with the grid

    @Input()
    focusFirst: boolean;

    @Input()
    viewOnly: boolean;

    @Input()
    dense: boolean = false;

    @Input()
    noWrap: boolean = false;

    @Input()
    index: number;

    @Input()
    isMenu: boolean;

    @Input()
    compact: boolean = false;

    @Input()
    onPanelGrid: boolean = false;

    /**
     * NOTE: Can't name it `dataController`, it seems that angularJs eats data-controller attributes somehow.
     * I can't find it in the angularJs source code, but when changed to `dataController`,
     * the binding is never resolved.
     */
    @Input()
    datacontroller: RepositoryFormData;

    @Output()
    modifierCallback = new EventEmitter<ModifierCommandArgs>();

    /** Available in the Template */ rowArray: IPanelRow[] = [];

    private _firstFocusable: PropertyMetadata;

    private properties: MetadataIntermediary[];

    private hiddenProperties = new Set<string>();

    private _isInitialized: boolean = false;

    private compactMode: boolean = false;

    doneLoading: boolean = false;

    panelId: string;

    constructor()
    {
        const $metadataEventService = this.$metadataEventService;

        const self = this;
        this.panelId = Utilities.generateRandomGuid();
        self.$metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                if (!args.Recalculating)
                {
                    self.onMetadataUpdate(true)
                        .finally(() => self.doneLoading = true);
                }
            });

        this.$metadataService.onProxyPropertyChanged.pipe(takeUntilDestroyed(), filter((arg: ModelChangeArg) => this.$metadataService.compareModels(arg.Model, this.model) && this.hiddenProperties.has(arg.Property.toString())))
            .subscribe(() =>
            {
                self.refreshPropertiesForRow();
            });
    }

    ngOnInit(): void
    {
        const self = this;
        this.onMetadataUpdate(true)
            .finally(() =>
            {
                self.doneLoading = true;
            });
        this.compactMode = this.$securityService.IsCompactMode;
    }

    ngAfterViewInit()
    {
        this._isInitialized = true;
    }

    ngDoCheck(): void
    {
        if (this.modelGroup && this.model)
            this.model["$$formValid"] = this.modelGroup.valid;
    }

    ngOnChanges(changesObj: SimpleChanges): void
    {
        if (!this._isInitialized)
        {
            return;
        }
        const self = this;
        self.doneLoading = false;
        const changedKeys = Object.keys(changesObj);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    this.onMetadataUpdate(true)
                        .finally(() =>
                        {
                            setTimeout(() =>
                            {
                                self.doneLoading = true;
                            }, 2);
                        });
                    break;
                case "metadata":
                    const change = changesObj[key];
                    const isUpdate = change.previousValue !== change.currentValue;
                    this.onMetadataUpdate(isUpdate)
                        .finally(() =>
                        {
                            setTimeout(() =>
                            {
                                self.doneLoading = true;
                            }, 2);
                        });
                    break;
                default:
                    self.doneLoading = true;
                    break;
            }
        }
    }

    private reloadProperties(): void
    {
        this.hiddenProperties.clear();
        const modelParent = this.model ? this.model.$$Parent : null;
        this.properties = this.$metadataService
            .FlattenProperties(this.metadata, modelParent, this.model)
            .filter(x => !this.$metadataService.IsHiddenProperty(x.property))
            .sort((a, b) => a.property.RenderPosition - b.property.RenderPosition);
        let currentRow = 0;
        let currentPercent = 0;
        this.rowArray = [];
        this.rowArray.push({Index: currentRow, Intermediaries: []});
        for (const prop of this.properties)
        {
            if (Utilities.IsNullOrUndefined(this._firstFocusable))
            {
                const propertyMetadata = prop.metadata.Properties.find(x => x.Name.toLowerCase() === prop.name.toLowerCase());
                if (propertyMetadata.InputType !== "Header") this._firstFocusable = propertyMetadata;
            }
            if (Utilities.IsNullOrUndefined(prop.property.DrawWidth))
            {
                prop.property.DrawWidth = 100;
            }
            if (Utilities.IsNullOrUndefined(prop.property.Spacer))
            {
                prop.property.Spacer = 0;
            }
            if (currentPercent + prop.property.DrawWidth + prop.property.Spacer > 100)
            {
                currentRow++;
                currentPercent = 0;
                this.rowArray.push({Index: currentRow, Intermediaries: []});
            }
            prop["$VisibleInPanel"] = !this.IsHidden(prop) || this.MaintainWidthWhenHidden(prop);
            if (prop.property.HiddenProperty) {
                const hiddenProp = prop.property.HiddenProperty.startsWith('!')
                    ? prop.property.HiddenProperty.substring(1)
                    : prop.property.HiddenProperty;
                this.hiddenProperties.add(hiddenProp);
            }
            this.rowArray.find(r => r.Index === currentRow).Intermediaries.push(prop);
            currentPercent += prop.property.DrawWidth + prop.property.Spacer;
        }
    }

    IsHidden(i: MetadataIntermediary): boolean
    {
        if (this.$metadataPropertyService.isHidden(i.property, i.model, i.parent) && !this.MaintainWidthWhenHidden(i)) return true;

        if (i.property.DrawWidth === -1)
        {
            return false;
        }
        return false;
    }

    MaintainWidthWhenHidden(i: MetadataIntermediary): boolean
    {
        return this.$metadataPropertyService.getMaintainWidthWhenHidden(i.property);
    }

    DrawWidth(i: MetadataIntermediary): any
    {
        if (this.$metadataPropertyService.isHidden(i.property, i.model, i.parent) && !this.MaintainWidthWhenHidden(i)) return 0;

        if (i.property.DrawWidth === -1)
        {
            return "auto";
        }
        return i.property.DrawWidth + i.property.Spacer;
    }

    getWidthStyle(metadataIntermediary: MetadataIntermediary): { width: string; }
    {
        return { width: this.DrawWidth(metadataIntermediary) + "%" };
    }

    doMetadataModifier($event: ModifierCommandArgs): void
    {
        const args = <ModifierCommandArgs>{
            model: this.model,
            propertyChanged: $event.propertyChanged,
            propertyValue: $event.propertyValue,
        };
        if (Utilities.IsNullOrUndefined(this.modifierCallback))
        {
            return;
        }
        this.modifierCallback.emit(args);
    }

    getPropertiesForRow(row: IPanelRow): MetadataIntermediary[]
    {
        if (Utilities.IsNullOrUndefined(row.Intermediaries))
            row.Intermediaries = [];
        return row.Intermediaries.filter(x => x["$VisibleInPanel"]);
    }

    refreshPropertiesForRow(): void
    {
        this.rowArray.forEach(r =>
        {
            r.Intermediaries.forEach(p => p["$VisibleInPanel"] = !this.IsHidden(p) || this.MaintainWidthWhenHidden(p));
        });
    }

    isFirstFocusable(fieldName: string): boolean
    {
        if (!this._firstFocusable)
        {
            return false;
        }
        return this._firstFocusable.Name.toLowerCase() === fieldName.toLowerCase();
    }

    private onMetadataUpdate(isUpdate: boolean): Promise<any>
    {
        const self = this;
        if (!isUpdate)
        {
            return Promise.resolve();
        }
        if (Utilities.IsNullOrUndefined(this.metadata) || Utilities.IsNullOrUndefined(this.model))
        {
            this.properties = [];
            return Promise.resolve();
        }
        return this.$metadataService.VerifyLoadChildren(this.metadata.Properties, this.metadata.TypeName, this.model)
            .then(x =>
            {
                self.model = x;
                this.reloadProperties();
            });
    }

    getSummaryPadding(): string
    {
        const padding = this.actionCount * 52; //52 it the total width each action icon takes right. 24 + 16 padding + 12 margin
        return padding.toString() + "px";
    }

    trackByRow: TrackByFunction<IPanelRow> = (index, row) => row.Index;

    trackByProperty: TrackByFunction<MetadataIntermediary> = (index, property) => property.name;

}

@Component({
    selector: "metadata-summary-panel",
    templateUrl: "./metadataSummaryPanel.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataSummaryPanelComponent extends MetadataPanelComponent
{

}
